import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { motion } from 'framer-motion';
import { Lock, Mail, Eye, EyeOff, Loader2, ArrowRight, Shield } from 'lucide-react';
import { supabase } from './supabaseClient';
import handPhoneImage from './asset/hand_phone.png';

const Login = () => {
    const { t, language } = useLanguage();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [tosAccepted, setTosAccepted] = useState(false);
    const [error, setError] = useState('');

    const [showEmailForm, setShowEmailForm] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const checkSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
                // App.js gate will redirect to /dashboard or /onboarding based on onboarding status
                navigate('/dashboard');
            }

        };
        checkSession();
    }, [navigate]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const errorDescription = params.get('error_description');
        if (errorDescription) {
            setError(decodeURIComponent(errorDescription));
        }
    }, [location]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const { error } = await supabase.auth.signInWithPassword({
                email,
                password
            });
            if (error) throw error;
            navigate('/dashboard');
        } catch (err) {
            setError(err.message || t.auth.login.errors.generic);
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!email) {
            setError(t.auth.login.errors.no_email);
            return;
        }
        setLoading(true);
        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email);
            if (error) throw error;
            navigate(`/reset-password?email=${encodeURIComponent(email)}`);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setLoading(true);
        setError('');
        try {
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: `${window.location.origin}/onboarding?lang=${language}`

                }
            });
            if (error) throw error;
        } catch (err) {
            setError(err.message || t.auth.login.errors.generic);
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left Side - Dark Login Form */}
            <div className="w-full lg:w-[45%] bg-slate-950 flex flex-col justify-between p-8 lg:p-12">
                <div>
                    <Link to="/" className="text-xl font-medium text-white hover:text-emerald-400 transition-colors font-logo">
                        Noctra
                    </Link>
                </div>

                <div className="flex-1 flex items-center justify-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="w-full max-w-sm flex flex-col items-center"
                    >
                        <h1 className="text-[32px] font-medium text-white mb-2">
                            {t.auth?.login?.title || (t.landing?.auth?.login?.title)}
                        </h1>
                        <p className="text-slate-400 text-[17px] mb-10">
                            {t.auth?.login?.subtitle || (t.landing?.auth?.login?.subtitle)}
                        </p>

                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className={`w-full p-4 rounded-xl mb-6 text-sm backdrop-blur-sm ${error.includes('Kolla') ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}
                            >
                                {error}
                            </motion.div>
                        )}

                        {!showEmailForm ? (
                            <div className="w-full space-y-4">
                                <button
                                    onClick={handleGoogleLogin}
                                    type="button"
                                    className="w-full flex justify-center items-center py-3.5 px-4 border border-[#2b2b2b] rounded-xl bg-transparent text-[15px] font-medium text-white hover:bg-white/5 transition-colors focus:outline-none"
                                >
                                    <svg className="h-5 w-5 mr-3" aria-hidden="true" viewBox="0 0 24 24">
                                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                        <path d="M1 1h22v22H1z" fill="none" />
                                    </svg>
                                    {t.auth?.login?.google || (t.landing?.auth?.login?.google)}
                                </button>

                                <button
                                    onClick={() => { }} /* Not implemented yet */
                                    type="button"
                                    className="w-full flex justify-center items-center py-3.5 px-4 border border-[#2b2b2b] rounded-xl bg-transparent text-[15px] font-medium text-white hover:bg-white/5 transition-colors focus:outline-none"
                                >
                                    <svg className="h-[22px] w-[22px] mr-3" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M16.365 14.363c-.021-2.95 2.4-4.366 2.51-4.436-1.373-2.016-3.488-2.29-4.25-2.338-1.808-.184-3.541 1.071-4.464 1.071-.925 0-2.365-1.045-3.876-1.02-1.968.026-3.784 1.152-4.8 2.923-2.053 3.585-.526 8.89 1.486 11.83.985 1.428 2.138 3.024 3.655 2.969 1.464-.055 2.025-.944 3.79-.944 1.766 0 2.29.944 3.82.916 1.554-.029 2.553-1.455 3.535-2.898 1.134-1.666 1.598-3.284 1.62-3.37-.037-.015-3.14-1.22-3.18-4.703h.001zM14.61 5.385c.808-.987 1.353-2.365 1.205-3.744-1.18.048-2.61.79-3.438 1.762-.741.868-1.34 2.261-1.173 3.626 1.319.102 2.602-.656 3.406-1.644z" />
                                    </svg>
                                    {t.auth.login.apple}
                                </button>

                                <div className="py-2 relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-[#2b2b2b]"></div>
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-4 bg-slate-950 text-slate-400">{t.auth.login.or}</span>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => setShowEmailForm(true)}
                                    className="w-full flex justify-center items-center py-3.5 px-4 border border-[#2b2b2b] rounded-xl bg-transparent text-[15px] font-medium text-white hover:bg-white/5 transition-colors focus:outline-none"
                                >
                                    <Mail className="h-5 w-5 mr-3 text-white" fill="none" strokeWidth={1.5} />
                                    {t.auth.login.email_btn}
                                </button>

                                <div className="pt-8 text-center w-full">
                                    <p className="text-[#a1a1aa] text-[15px]">
                                        {t.auth.login.no_account}{' '}
                                        <Link
                                            to="/signup"
                                            className="text-white underline underline-offset-[3px] decoration-1 hover:text-[#a1a1aa] transition-colors"
                                        >
                                            {t.auth.login.signup_link}
                                        </Link>
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="w-full">
                                <form onSubmit={handleSubmit} className="space-y-5 text-left mb-6">
                                    <div>
                                        <label className="block text-sm text-slate-400 mb-2 font-medium">{t.auth.login.email_label}</label>
                                        <div className="relative">
                                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="w-full pl-11 pr-4 py-3.5 bg-slate-900/50 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all outline-none"
                                                placeholder="din@email.se"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm text-slate-400 mb-2 font-medium">{t.auth.login.password_label}</label>
                                        <div className="relative">
                                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="w-full pl-11 pr-12 py-3.5 bg-slate-900/50 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all outline-none"
                                                placeholder="••••••••"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                                            >
                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="flex justify-end">
                                        <button
                                            type="button"
                                            onClick={handleForgotPassword}
                                            className="text-sm text-slate-500 hover:text-emerald-400 transition-colors"
                                        >
                                            {t.auth.login.forgot_password}
                                        </button>
                                    </div>

                                    <div className="flex items-start gap-3 mt-4 mb-2">
                                        <input
                                            type="checkbox"
                                            id="tos-login"
                                            checked={tosAccepted}
                                            onChange={(e) => setTosAccepted(e.target.checked)}
                                            className="mt-0.5 w-4 h-4 rounded border-slate-700 bg-slate-900/50 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-slate-950 cursor-pointer"
                                        />
                                        <label htmlFor="tos-login" className="text-sm text-slate-400 leading-tight cursor-pointer">
                                            Jag godkänner <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline">Användarvillkoren</a> och <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline">Integritetspolicyn</a>.
                                        </label>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading || !tosAccepted}
                                        className={`w-full font-semibold py-3.5 rounded-xl transition-all flex items-center justify-center gap-2 group shadow-lg ${
                                            (!tosAccepted || loading)
                                                ? 'bg-slate-800 text-slate-500 cursor-not-allowed opacity-60'
                                                : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white shadow-emerald-500/20 hover:shadow-emerald-500/30 hover:scale-[1.02] active:scale-[0.98]'
                                        }`}
                                    >
                                        {loading ? (
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                        ) : (
                                            <>
                                                {t.auth.login.submit}
                                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                            </>
                                        )}
                                    </button>
                                </form>

                                <button
                                    onClick={() => setShowEmailForm(false)}
                                    className="w-full text-center text-sm text-slate-400 hover:text-white transition-colors"
                                >
                                    ← {t.auth.login.back}
                                </button>
                            </div>
                        )}
                    </motion.div>
                </div>

                <div className="text-xs text-slate-600 leading-relaxed">
                    <p>
                        {t.auth.footer.agree}{' '}
                        <Link to="/terms" className="text-slate-500 hover:text-slate-400 underline">{t.auth.footer.terms}</Link>{' '}
                        {t.auth.footer.and}{' '}
                        <Link to="/privacy" className="text-slate-500 hover:text-slate-400 underline">{t.auth.footer.privacy}</Link>.
                    </p>
                </div>
            </div>

            {/* Right Side - Dark Moody Image */}
            <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden min-h-screen bg-neutral-800">
                {/* Base image */}
                <img
                    src={handPhoneImage}
                    alt={t.auth.visual.alt}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                        filter: 'brightness(0.35) contrast(1.1) saturate(0.3)',
                    }}
                />

                {/* Vignette overlay for dramatic shadows */}
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.5) 100%)',
                    }}
                />

                {/* Left edge blend into dark login side */}
                <div className="absolute inset-0 bg-gradient-to-r from-slate-950/60 via-transparent to-transparent" />

                {/* Bottom gradient for text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

                {/* Centered Noctra logo on phone screen */}
                <div className="absolute inset-0 flex items-center justify-center pb-28">
                    <motion.span
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.3 }}
                        className="text-white text-5xl font-medium tracking-wide font-logo"
                        style={{ textShadow: '0 2px 30px rgba(0,0,0,0.6)' }}
                    >
                        Noctra
                    </motion.span>
                </div>

                {/* Bottom text */}
                <div className="absolute bottom-12 left-0 right-0 px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.6 }}
                    >
                        <h2
                            className="text-2xl font-semibold text-white mb-3"
                            style={{ textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}
                        >
                            {t.auth.visual.title}
                        </h2>
                        <p className="text-white/70 text-sm leading-relaxed max-w-md">
                            {t.auth.visual.description}
                        </p>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default Login;
